import { useEffect, useState } from 'react';
import { getDelayValueFromRange } from './delay-utils';

export interface UseContentStreamProps {
  content: string;
  enabled?: boolean;
  splitBy?: 'character' | 'word';
  tokenDelayRange?: [number] | [number, number];
}

export interface ContentStreamOutput {
  isDisabled: boolean;
  isEnded: boolean;
  isIdle: boolean;
  isStreaming: boolean;
  lastToken: string;
  markdownStream: string;
  rawStream: string;
  status: StreamStatus;
}

export enum StreamStatus {
  Disabled = 'disabled',
  Ended = 'ended',
  Idle = 'idle',
  Streaming = 'streaming',
}

const CARET_HTML = '<caret>&nbsp;</caret>';

const parseContent = (content: string): string => {
  return content.trim();
};

const formatMarkdownContent = (content: string): string => {
  return `${content}${CARET_HTML}`;
};

export const useContentStream = ({
  content,
  enabled = true,
  splitBy = 'character',
  tokenDelayRange = [25],
}: UseContentStreamProps): ContentStreamOutput => {
  const [status, setStatus] = useState<StreamStatus>(StreamStatus.Idle);
  const [rawStream, setRawStream] = useState('');
  const [markdownStream, setMarkdownStream] = useState('');
  const [lastToken, setLastToken] = useState('');

  const parsedContent = parseContent(content);

  useEffect(() => {
    let isMounted = true;
    setMarkdownStream('');
    if (!enabled) {
      setStatus(StreamStatus.Disabled);
      return () => {
        isMounted = false;
        setStatus(StreamStatus.Idle);
      };
    }
    setStatus(StreamStatus.Idle);

    const streamContent = async () => {
      setStatus(StreamStatus.Streaming);
      let tokens: string[];
      if (splitBy === 'word') {
        // Split into words and spaces
        tokens = parsedContent.match(/\S+|\s+/g) || [];
      } else {
        tokens = parsedContent.split('');
      }
      for (let i = 0; i < tokens.length; i++) {
        if (!isMounted) return;
        // Build buffer: all previous tokens as plain text
        let buffer = '';
        for (let j = 0; j < i; j++) {
          buffer += tokens[j];
        }
        const token = tokens[i];
        setLastToken(token);

        let markdownToken;
        if (splitBy === 'word') {
          if (/^\s+$/.test(token)) {
            markdownToken = token;
          } else {
            markdownToken = `<token>${token}</token>`;
          }
        } else {
          if (token === ' ') {
            markdownToken = token;
          } else {
            markdownToken = `<token>${token}</token>`;
          }
        }

        const nextRawBuffer = buffer + token;
        const nextMarkdownBuffer = buffer + markdownToken;
        const markdownStream = formatMarkdownContent(nextMarkdownBuffer);
        setMarkdownStream(markdownStream);
        setRawStream(nextRawBuffer);

        await new Promise((d) =>
          setTimeout(d, getDelayValueFromRange(tokenDelayRange))
        );
      }
      if (isMounted) {
        setStatus(StreamStatus.Ended);
        setMarkdownStream(parsedContent);
      }
    };
    streamContent();
    return () => {
      isMounted = false;
      setStatus(StreamStatus.Idle);
    };
  }, [content, splitBy, enabled, parsedContent, tokenDelayRange]);

  return {
    isDisabled: status === StreamStatus.Disabled,
    isEnded: status === StreamStatus.Ended,
    isIdle: status === StreamStatus.Idle,
    isStreaming: status === StreamStatus.Streaming,
    lastToken,
    markdownStream,
    rawStream,
    status,
  };
};
